import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import MatchedJobs from "./MatchedJobs/MatchedJobs";
import ActivityTracker from "./ActivityTracker/ActivityTracker";
import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";
import { getNetworkChatContact } from "../../../../endpoints/referral.service";
import ChatList from "../../../MessagesPage/components/ChatList";
import dayjs from "@lib/dayjs";

const tabs = [
  {
    label: "Matched Jobs",
  },
  {
    label: "Tracker",
  },
  {
    label: "Messages",
  },
];

const NetworkDetailLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { candidateId: contactId } = useParams();
  const [search, setSearch] = useSearchParams();
  const activeTab = search.get("tab") === "messages" ? 2 : search.get("tab") === "tracker" ? 1 : 0;
  const initialMember = location?.state?.member || location?.state;
  const [member, setMember] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setMember(null);
    setLoading(true);
    setError("");
    if (!contactId || !/^[a-f\d]{24}$/i.test(contactId)) {
      setError("This network contact is unavailable. Please reopen it from your network list.");
      setLoading(false);
      return;
    }
    getNetworkChatContact(contactId).then(response => {
      if (cancelled) return;
      if (!response?.success || !response?.data?.userId) throw new Error("This network contact is unavailable.");
      setMember({ ...initialMember, ...response.data });
    }).catch(() => {
      if (!cancelled) setError("This network contact is unavailable or is no longer in your network.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [contactId]);

  const selectedContact: any = member ? {
    jobId: member.conversationKey, jobTitle: "General conversation",
    participantDetails: { userId: member.userId, _id: member.userId,
      name: [member.firstName, member.lastName].filter(Boolean).join(" "),
      profilePic: member.profilePic, referenceId: member.refNo, companyName: member.companyName },
  } : null;

  return (
    <div className="flex flex-col items-center rounded-lg bg-white w-full flex-grow p-5 gap-6">
      <button
        onClick={() => navigate(initialMember?.networkKind === "recruiter" || (member && !member.candidateId)
          ? "/network/my-recruiter-network" : "/network/my-network")}
        className="flex items-center self-start"
      >
        <ChevronLeftIcon className={"w-4 h-4"} fill="#4d4d4d" />
        <label className="text-xs font-medium text-charcoal cursor-pointer">
          Back to listing
        </label>
      </button>
      <div className="flex items-center border-b w-full pb-2 gap-10 pl-[22.5%]">
        {tabs?.map((tab, tabIndex) => {
          const isActive = tabIndex === activeTab;
          return (
            <button
              key={tab?.label}
              onClick={() => {
                const next = new URLSearchParams(search);
                next.set("tab", ["matches", "tracker", "messages"][tabIndex]);
                setSearch(next, { replace: true });
              }}
              className={`font-semibold text-xs ${
                isActive
                  ? "text-charcoal border-b border-primary"
                  : "text-light-grey"
              }  pb-0.5`}
            >
              {tab?.label}
            </button>
          );
        })}
      </div>
      {loading ? <p className="p-6 text-sm">Loading network contact...</p>
        : error ? <p role="alert" className="p-6 text-sm">{error}</p>
        : <div className="flex w-full flex-1 p-1 min-h-0">
        <aside aria-label="Network contact details" className="flex flex-col w-[20%] border-r gap-7 pr-4 break-words">
          {member?.profilePic ? (
            <img src={member?.profilePic} alt="Contact profile" className="h-28 w-28 rounded-full" />
          ) : (
            <div className="h-28 w-28 font-bold text-light-black flex items-center justify-center bg-gray-100 rounded-full capitalize text-6xl">
              {member?.firstName?.charAt(0) || "T"}
            </div>
          )}
          <span className="font-medium text-charcoal text-xs">{member?.refNo}</span>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-charcoal capitalize text-xs">
              {member?.firstName}{" "}{member?.lastName}
            </span>
            <span className="font-bold text-xs">
              {member?.jobTitle}
            </span>
          </div>
          {member?.companyName && <div className="flex flex-col gap-1 text-xs">
            <span className="font-bold">Company</span>
            <span className="text-charcoal">{member.companyName}</span>
          </div>}
          {member?.location && <div className="flex flex-col gap-1 text-xs">
            <span className="font-bold">Location</span>
            <span className="text-charcoal">{member.location}</span>
          </div>}
          {member?.lastSeen && dayjs(member.lastSeen).isValid() && <span className="text-charcoal italic text-xs">
            Last seen {dayjs(member.lastSeen).fromNow()}
          </span>}
          {member?.status && <div className="flex flex-col gap-1 text-xs">
            <span className="font-bold">Status</span>
            <span className="text-charcoal capitalize">{member.status.toLowerCase().replace(/_/g, " ")}</span>
          </div>}
          {member?.memberSince && dayjs(member.memberSince).isValid() && <div className="flex flex-col gap-1 text-xs">
            <span className="font-bold">Member since</span>
            <span className="text-charcoal">{dayjs(member.memberSince).format("DD MMM YYYY")}</span>
          </div>}
          {member?.aboutDetails && <div className="flex flex-col gap-1 text-xs">
            <span className="font-bold">About</span>
            <span className="text-charcoal whitespace-pre-wrap">{member.aboutDetails}</span>
          </div>}
        </aside>
        {/* Outlet container */}
        <div className="flex flex-col w-[80%] min-h-0">
          {activeTab === 2 ? <ChatList key={`${member.userId}:${member.conversationKey}`} selectedContact={selectedContact} />
            : activeTab === 1 ? <ActivityTracker />
            : <MatchedJobs member={member} />}
        </div>
      </div>}
    </div>
  );
};

export default NetworkDetailLayout;
